import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Statistic, Alert } from 'antd';
import {
  DollarOutlined,
  ThunderboltOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { config } from '../config/env';
import { savingsReasonText } from './InsufficientDataCard';

const { Title, Text } = Typography;

interface MeteredComparison {
  period: string;
  kWhReduced: number | null;
  dollarsSaved: number | null;
  co2Reduced: number | null;
  method: string;
}

/**
 * P1 M&V rewrite.
 *
 * The previous implementation fabricated the entire comparison on both paths:
 * hardcoded 1000 kWh usage, "assume 15% savings", invented before/after peaks
 * and efficiency scores — plus a full fallback block on error.
 *
 * What CAN be shown honestly: the metered delta between the reporting window
 * and its fitted pre-period baseline (kWh, dollars via real tariff, CO2).
 */
export const BeforeAfterComparison: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [comparison, setComparison] = useState<MeteredComparison | null>(null);
  const [reason, setReason] = useState<string | null>(null);

  useEffect(() => {
    fetchComparisonData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const fetchComparisonData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${config.apiUrl}/api/energy/savings/metrics?days=30`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const payload = response.data.savings;
      if (payload?.status === 'ok' && payload.metrics) {
        setComparison({
          period: '30 days',
          kWhReduced: payload.metrics.kWhReduced ?? null,
          dollarsSaved: payload.metrics.dollarsSaved ?? null,
          co2Reduced: payload.metrics.co2Reduced ?? null,
          method:
            (payload.provenance && (payload.provenance.method as string)) ||
            'matched_period_v1',
        });
        setReason(null);
      } else {
        setComparison(null);
        setReason(savingsReasonText(payload));
      }
    } catch (error) {
      console.error('Failed to fetch comparison data:', error);
      setComparison(null);
      setReason('Savings service unreachable — no figures are shown.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);

  const display = (v: number | null, fmt?: (n: number) => string) =>
    v !== null ? (fmt ? fmt(v) : v.toFixed(1)) : '—';

  if (loading) {
    return <Card loading />;
  }

  if (!comparison) {
    return (
      <Card>
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <Title level={3}>Before &amp; After Comparison</Title>
        </div>
        <Alert
          message="No verified comparison yet"
          description={
            reason || 'Metered data is not available for this period.'
          }
          type="info"
          showIcon
        />
      </Card>
    );
  }

  return (
    <Card>
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <Title level={3}>Measured vs Fitted Baseline</Title>
        <Text type="secondary">
          Reporting window compared against the prior metered period (
          {comparison.method})
        </Text>
      </div>

      <Row gutter={[24, 24]}>
        <Col xs={24} md={8}>
          <Card size="small" style={{ textAlign: 'center', height: '100%' }}>
            <Statistic
              title="Energy Reduction"
              value={display(comparison.kWhReduced)}
              suffix="kWh"
              prefix={<ThunderboltOutlined />}
              valueStyle={{
                color:
                  comparison.kWhReduced !== null && comparison.kWhReduced >= 0
                    ? '#3f8600'
                    : '#cf1322',
              }}
            />
            <Text
              type="secondary"
              style={{ fontSize: 12, display: 'block', marginTop: 8 }}
            >
              {comparison.kWhReduced !== null && comparison.kWhReduced < 0
                ? 'Consumption increased vs baseline'
                : `vs fitted baseline, ${comparison.period}`}
            </Text>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small" style={{ textAlign: 'center', height: '100%' }}>
            <Statistic
              title="Dollar Savings"
              value={display(comparison.dollarsSaved, formatCurrency)}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
            <Text
              type="secondary"
              style={{ fontSize: 12, display: 'block', marginTop: 8 }}
            >
              Real tariff rate applied to the metered delta
            </Text>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small" style={{ textAlign: 'center', height: '100%' }}>
            <Statistic
              title="CO₂ Avoided"
              value={display(comparison.co2Reduced)}
              suffix="tCO₂e"
              prefix={<EnvironmentOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
            <Text
              type="secondary"
              style={{ fontSize: 12, display: 'block', marginTop: 8 }}
            >
              Documented grid factor × metered kWh
            </Text>
          </Card>
        </Col>
      </Row>

      <Alert
        style={{ marginTop: '24px' }}
        message="Why there is no before/after chart"
        description="Historical before/after visuals require a longer metered record than exists today. Only the measured delta against the fitted baseline is shown — nothing here is estimated."
        type="info"
        showIcon
      />
    </Card>
  );
};

export default BeforeAfterComparison;